/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { Text } from '../text';
import './googleBtn.css';

type ButtonProps = {
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const LightButton = styled.button<ButtonProps>`
  padding-inline: ${({ theme }) => theme.spacing(10)};
  height: 64px;
  cursor: pointer;
  color: #fff;
  font-size: 16px;
  border-radius: 76px;
  border: 1.333px solid #2e2d33;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
`;
const Gradient = styled.div`
  position: absolute;
  right: -10px;
  bottom: -10px;
`;

export const DangerButton = styled.button<ButtonProps>`
  padding-inline: ${({ theme }) => theme.spacing(10)};
  height: 45px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.dark};
  font-size: 16px;
  border-radius: 76px;
  background-color: ${({ theme }) => theme.colors.accent['red/main']};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

export const Button: React.FC<ButtonProps> = ({ children, ...props }) => {
  return <LightButton {...props}>{children}</LightButton>;
};

export const ButtonWithGradient: React.FC<ButtonProps> = ({
  children,
  ...props
}) => {
  return (
    <LightButton {...props}>
      {children}
      <Gradient>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="159"
          height="64"
          viewBox="0 0 159 64"
          fill="none"
        >
          <g filter="url(#filter0_f_6983_2385)">
            <ellipse
              cx="156.333"
              cy="104"
              rx="97.3334"
              ry="76"
              fill="#313131"
              fill-opacity="0.5"
            />
          </g>
          <defs>
            <filter
              id="filter0_f_6983_2385"
              x="0.33334"
              y="-30.6667"
              width="312"
              height="269.333"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="BackgroundImageFix"
                result="shape"
              />
              <feGaussianBlur
                stdDeviation="29.3333"
                result="effect1_foregroundBlur_6983_2385"
              />
            </filter>
          </defs>
        </svg>
      </Gradient>
    </LightButton>
  );
};

export const GoogleOauthBtn: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ children, ...props }) => {
  return (
    <ButtonWithGradient {...props}>
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 48 48"
        style={{
          display: 'block',
          width: '30px',
          height: '30px',
          marginRight: '20px',
        }}
      >
        <path
          fill="#EA4335"
          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        ></path>
        <path
          fill="#4285F4"
          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
        ></path>
        <path
          fill="#FBBC05"
          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        ></path>
        <path
          fill="#34A853"
          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        ></path>
        <path fill="none" d="M0 0h48v48H0z"></path>
      </svg>
      <Text.Button>Connect Google</Text.Button>
    </ButtonWithGradient>
  );
};

export const GoogleSignInBtn: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ children, ...props }) => {
  return (
    <button className="gsi-material-button" {...props}>
      <div className="gsi-material-button-state"></div>
      <div className="gsi-material-button-content-wrapper">
        <div className="gsi-material-button-icon">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            style={{ display: 'block' }}
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            ></path>
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            ></path>
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            ></path>
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            ></path>
            <path fill="none" d="M0 0h48v48H0z"></path>
          </svg>
        </div>
        <span className="gsi-material-button-contents">
          Sign up with Google
        </span>
        <span style={{ display: 'none' }}>Sign up with Google</span>
      </div>
    </button>
  );
};

export default Button;
export * from './newGoogleButton';
export * from './microsoftButton';
export * from './plainButton';
export * from './lightButton';
